import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { UserRoundX, X } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintContent, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { formSnapshotDirty } from "@/lib/formDirty";
import { CreateUserForm } from "@/pages/admin/CreateUser";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  fetchWorkspaceAvatarSyncStatus,
  startWorkspaceOAuth,
  syncAllWorkspaceAvatars,
  syncSelectedWorkspaceAvatars,
} from "@/lib/workspaceAvatarSync";

type DirectoryRowKind = "both" | "user_only" | "staff_only";

interface DirectoryRow {
  rowKind: DirectoryRowKind;
  userId: string | null;
  authEmail: string | null;
  userSetting: {
    displayName: string | null;
    orgId: number | null;
    roleId: number | null;
    roleIds?: number[];
    fsUserId: string | null;
    quickbooksTimeUserId: number | null;
    avatarUrl: string | null;
    avatarSyncedAt: string | null;
  } | null;
  staff: {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string | null;
    active: boolean;
    sbUserId: string | null;
    fsUserId: string | null;
  } | null;
}

function rowKey(row: DirectoryRow): string {
  if (row.rowKind === "staff_only" && row.staff) {
    return `s:${row.staff.id}`;
  }
  if (row.userId) {
    return `u:${row.userId}`;
  }
  return `unknown:${row.staff?.id ?? "?"}`;
}

function directoryPersistedRoleIds(row: DirectoryRow): number[] {
  if (!row.userSetting) {
    return [];
  }
  if (row.userSetting.roleIds && row.userSetting.roleIds.length > 0) {
    return [...row.userSetting.roleIds].sort((left, right) => left - right);
  }
  return row.userSetting.roleId != null ? [row.userSetting.roleId] : [];
}

function directoryEditDirty(input: {
  editingRow: DirectoryRow;
  editDisplayName: string;
  editOrgId: number | "";
  editRoleIds: number[];
  editUserFsId: string;
  editQuickBooksTimeUserId: string;
  editStaffFirst: string;
  editStaffLast: string;
  editStaffEmail: string;
  editStaffActive: boolean;
  editStaffSb: string;
  editStaffFs: string;
}): boolean {
  const row = input.editingRow;
  if (row.userId && row.userSetting) {
    if (input.editDisplayName !== (row.userSetting.displayName ?? "")) {
      return true;
    }
    if (input.editOrgId !== (row.userSetting.orgId ?? "")) {
      return true;
    }
    const persistedRoles = directoryPersistedRoleIds(row);
    const draftRoles = [...input.editRoleIds].sort((left, right) => left - right);
    if (formSnapshotDirty(draftRoles, persistedRoles)) {
      return true;
    }
    if (input.editUserFsId !== (row.userSetting.fsUserId ?? "")) {
      return true;
    }
    const persistedQuickBooks =
      row.userSetting.quickbooksTimeUserId != null ? String(row.userSetting.quickbooksTimeUserId) : "";
    if (input.editQuickBooksTimeUserId !== persistedQuickBooks) {
      return true;
    }
  }
  if (row.staff) {
    if (input.editStaffFirst !== row.staff.firstName) {
      return true;
    }
    if (input.editStaffLast !== row.staff.lastName) {
      return true;
    }
    if (input.editStaffEmail !== (row.staff.email ?? "")) {
      return true;
    }
    if (input.editStaffActive !== row.staff.active) {
      return true;
    }
    if (input.editStaffSb !== (row.staff.sbUserId ?? "")) {
      return true;
    }
    if (input.editStaffFs !== (row.staff.fsUserId ?? "")) {
      return true;
    }
  }
  return false;
}

type Filter = "all" | DirectoryRowKind;

function organisationLabel(
  orgId: number | null | undefined,
  orgList: { id: number; name: string | null }[],
): string {
  if (orgId == null) return "—";
  const organisation = orgList.find((item) => item.id === orgId);
  const name = organisation?.name?.trim();
  return name && name.length > 0 ? name : `Organisation ${orgId}`;
}

function roleLabel(
  roleId: number | null | undefined,
  roleList: { id: number; name: string | null }[],
): string {
  if (roleId == null) return "—";
  const role = roleList.find((item) => item.id === roleId);
  const name = role?.name?.trim();
  return name && name.length > 0 ? name : `Role ${roleId}`;
}

function roleLabels(
  roleIds: number[] | null | undefined,
  primaryRoleId: number | null | undefined,
  roleList: { id: number; name: string | null }[],
): string[] {
  const ids =
    roleIds && roleIds.length > 0
      ? roleIds
      : primaryRoleId != null
        ? [primaryRoleId]
        : [];
  return ids.map((id) => roleLabel(id, roleList));
}

function directoryDisplayName(row: DirectoryRow): string {
  const staffName = row.staff?.fullName?.trim();
  if (row.userSetting) {
    const fromProfile = row.userSetting.displayName?.trim();
    return fromProfile || staffName || "—";
  }
  return staffName || "—";
}

function directoryRowSearchHaystack(
  row: DirectoryRow,
  orgs: { id: number; name: string | null }[],
  roles: { id: number; name: string | null }[],
): string {
  const name = directoryDisplayName(row);
  const email = row.authEmail ?? row.staff?.email ?? "";
  const org = organisationLabel(row.userSetting?.orgId, orgs);
  const roleText = roleLabels(row.userSetting?.roleIds, row.userSetting?.roleId, roles).join(" ");
  const staffNames = [row.staff?.firstName, row.staff?.lastName, row.staff?.fullName]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .join(" ");
  const rowKindLabel =
    row.rowKind === "both"
      ? "integrator staff"
      : row.rowKind === "user_only"
        ? "no staff record"
        : "staff only";
  const active = row.staff ? (row.staff.active ? "active yes" : "active no") : "";
  return `${name} ${email} ${org} ${roleText} ${staffNames} ${rowKindLabel} ${active}`.toLowerCase();
}

function directoryRowMatchesSearch(
  row: DirectoryRow,
  query: string,
  orgs: { id: number; name: string | null }[],
  roles: { id: number; name: string | null }[],
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) {
    return true;
  }
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const haystack = directoryRowSearchHaystack(row, orgs, roles);
  return tokens.every((token) => haystack.includes(token));
}

/** True when this row is tied to a Supabase Auth identity (integrator profile and/or staff sb_user_id). */
function hasSupabaseAuthAccount(row: DirectoryRow): boolean {
  if (row.userId != null && row.userId !== "") {
    return true;
  }
  const sb = row.staff?.sbUserId?.trim();
  return Boolean(sb);
}

function labelChip(text: string, tone: "neutral" | "info"): ReactNode {
  if (text === "—") {
    return "—";
  }
  return (
    <Badge tone={tone} className="font-normal">
      {text}
    </Badge>
  );
}

export function UserDirectory() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const avatarStatusQuery = useQuery({
    queryKey: ["workspace-avatar-sync", "status"],
    queryFn: fetchWorkspaceAvatarSyncStatus,
    staleTime: 30_000,
  });
  const avatarOperator = avatarStatusQuery.data?.operatorEligible === true;
  const directoryQuery = useQuery({
    queryKey: ["user-admin", "directory"],
    queryFn: () => apiFetch<{ rows: DirectoryRow[] }>("/api/user-admin/directory"),
  });
  const orgsQuery = useQuery({
    queryKey: ["user-admin", "orgs"],
    queryFn: () => apiFetch<{ orgs: { id: number; name: string | null }[] }>("/api/user-admin/orgs"),
  });
  const rolesQuery = useQuery({
    queryKey: ["user-admin", "roles"],
    queryFn: () => apiFetch<{ roles: { id: number; name: string | null }[] }>("/api/user-admin/roles"),
  });

  const [filter, setFilter] = useState<Filter>("all");
  const [directorySearch, setDirectorySearch] = useState("");
  const [editingRow, setEditingRow] = useState<DirectoryRow | null>(null);
  const [avatarActionMessage, setAvatarActionMessage] = useState<string | null>(null);

  const connectWorkspaceMutation = useMutation({
    mutationFn: () =>
      startWorkspaceOAuth({
        returnTo: "/admin/hub-accounts",
        uiOrigin: window.location.origin,
      }),
    onSuccess: (payload) => {
      window.location.assign(payload.authorizeUrl);
    },
    onError: (error: unknown) => {
      setAvatarActionMessage(error instanceof Error ? error.message : "Could not start Workspace connect.");
    },
  });

  const refreshAllAvatarsMutation = useMutation({
    mutationFn: syncAllWorkspaceAvatars,
    onSuccess: (payload) => {
      setAvatarActionMessage(`Refreshed ${payload.updatedCount} avatar(s).`);
      void queryClient.invalidateQueries({ queryKey: ["workspace-avatar-sync", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "directory"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (error: unknown) => {
      setAvatarActionMessage(error instanceof Error ? error.message : "Avatar refresh failed.");
    },
  });

  const refreshOneAvatarMutation = useMutation({
    mutationFn: (userId: string) => syncSelectedWorkspaceAvatars([userId]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["workspace-avatar-sync", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "directory"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("workspaceAvatars");
    if (outcome === "connected") {
      setAvatarActionMessage("Google Workspace connected. You can refresh avatars now.");
      params.delete("workspaceAvatars");
      params.delete("workspaceAvatarsError");
      const nextSearch = params.toString();
      void navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
        { replace: true },
      );
      void queryClient.invalidateQueries({ queryKey: ["workspace-avatar-sync", "status"] });
    } else if (outcome === "error") {
      const message = params.get("workspaceAvatarsError") ?? "Workspace connect failed.";
      setAvatarActionMessage(message);
      params.delete("workspaceAvatars");
      params.delete("workspaceAvatarsError");
      const nextSearch = params.toString();
      void navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, queryClient]);

  const [editDisplayName, setEditDisplayName] = useState("");
  const [editOrgId, setEditOrgId] = useState<number | "">("");
  const [editRoleIds, setEditRoleIds] = useState<number[]>([]);
  const [editUserFsId, setEditUserFsId] = useState("");
  const [editQuickBooksTimeUserId, setEditQuickBooksTimeUserId] = useState("");
  const [deleteRemoveAuth, setDeleteRemoveAuth] = useState(false);

  const [editStaffFirst, setEditStaffFirst] = useState("");
  const [editStaffLast, setEditStaffLast] = useState("");
  const [editStaffEmail, setEditStaffEmail] = useState("");
  const [editStaffActive, setEditStaffActive] = useState(true);
  const [editStaffSb, setEditStaffSb] = useState("");
  const [editStaffFs, setEditStaffFs] = useState("");

  const [addStaffFirst, setAddStaffFirst] = useState("");
  const [addStaffLast, setAddStaffLast] = useState("");
  const [addStaffActive, setAddStaffActive] = useState(true);
  const [addStaffFs, setAddStaffFs] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const rows = directoryQuery.data?.rows ?? [];
  const orgs = orgsQuery.data?.orgs ?? [];
  const roles = rolesQuery.data?.roles ?? [];

  const filteredRows = useMemo(() => {
    const byKind = filter === "all" ? rows : rows.filter((row) => row.rowKind === filter);
    if (directorySearch.trim().length === 0) {
      return byKind;
    }
    return byKind.filter((row) => directoryRowMatchesSearch(row, directorySearch, orgs, roles));
  }, [rows, filter, directorySearch, orgs, roles]);

  function closeEditModal(): void {
    setEditingRow(null);
  }

  function beginEdit(row: DirectoryRow): void {
    setEditingRow(row);
    setDeleteRemoveAuth(false);
    if (row.userSetting && row.userId) {
      setEditDisplayName(row.userSetting.displayName ?? "");
      setEditOrgId(row.userSetting.orgId ?? "");
      setEditRoleIds(
        row.userSetting.roleIds && row.userSetting.roleIds.length > 0
          ? [...row.userSetting.roleIds]
          : row.userSetting.roleId != null
            ? [row.userSetting.roleId]
            : [],
      );
      setEditUserFsId(row.userSetting.fsUserId ?? "");
      setEditQuickBooksTimeUserId(
        row.userSetting.quickbooksTimeUserId != null ? String(row.userSetting.quickbooksTimeUserId) : "",
      );
    } else {
      setEditDisplayName("");
      setEditOrgId("");
      setEditRoleIds([]);
      setEditUserFsId("");
      setEditQuickBooksTimeUserId("");
    }
    if (row.staff) {
      setEditStaffFirst(row.staff.firstName);
      setEditStaffLast(row.staff.lastName);
      setEditStaffEmail(row.staff.email ?? "");
      setEditStaffActive(row.staff.active);
      setEditStaffSb(row.staff.sbUserId ?? "");
      setEditStaffFs(row.staff.fsUserId ?? "");
    } else {
      setEditStaffFirst("");
      setEditStaffLast("");
      setEditStaffEmail(row.authEmail ?? "");
      setEditStaffActive(true);
      setEditStaffSb(row.userId ?? "");
      setEditStaffFs("");
    }
    setAddStaffFirst("");
    setAddStaffLast("");
    setAddStaffActive(true);
    setAddStaffFs("");
  }

  const invalidateDirectory = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["user-admin", "directory"] });
  };

  const patchUserMutation = useMutation({
    mutationFn: (input: {
      id: string;
      displayName: string | null;
      orgId: number | null;
      roleIds: number[];
      fsUserId: string | null;
      quickbooksTimeUserId: number | null;
    }) =>
      apiFetch(`/api/user-admin/user-settings/${encodeURIComponent(input.id)}`, {
        method: "PATCH",
        body: {
          displayName: input.displayName,
          orgId: input.orgId,
          roleIds: input.roleIds,
          fsUserId: input.fsUserId,
          quickbooksTimeUserId: input.quickbooksTimeUserId,
        },
      }),
    onSuccess: () => {
      invalidateDirectory();
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (input: { id: string; removeAuth: boolean }) =>
      apiFetch(
        `/api/user-admin/user-settings/${encodeURIComponent(input.id)}?removeAuth=${input.removeAuth ? "true" : "false"}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      invalidateDirectory();
      setEditingRow(null);
    },
  });

  const addStaffMutation = useMutation({
    mutationFn: (input: {
      firstName: string;
      lastName: string;
      email: string | null;
      active: boolean;
      sbUserId: string | null;
      fsUserId: string | null;
    }) =>
      apiFetch("/api/user-admin/staff", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      invalidateDirectory();
      setEditingRow(null);
    },
  });

  const patchStaffMutation = useMutation({
    mutationFn: (input: {
      id: number;
      firstName: string;
      lastName: string;
      email: string | null;
      active: boolean;
      sbUserId: string | null;
      fsUserId: string | null;
    }) =>
      apiFetch(`/api/user-admin/staff/${input.id}`, {
        method: "PATCH",
        body: {
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          active: input.active,
          sbUserId: input.sbUserId,
          fsUserId: input.fsUserId,
        },
      }),
    onSuccess: () => {
      invalidateDirectory();
    },
  });

  const deleteStaffMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/user-admin/staff/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      invalidateDirectory();
      setEditingRow(null);
    },
  });

  const isSavingEditingRow = patchUserMutation.isPending || patchStaffMutation.isPending;
  const directoryDirty =
    editingRow != null &&
    directoryEditDirty({
      editingRow,
      editDisplayName,
      editOrgId,
      editRoleIds,
      editUserFsId,
      editQuickBooksTimeUserId,
      editStaffFirst,
      editStaffLast,
      editStaffEmail,
      editStaffActive,
      editStaffSb,
      editStaffFs,
    });

  async function saveEditingRow(): Promise<void> {
    if (!editingRow) return;

    if (editingRow.userId && editingRow.userSetting) {
      await patchUserMutation.mutateAsync({
        id: editingRow.userId,
        displayName: editDisplayName.trim() || null,
        orgId: editOrgId === "" ? null : editOrgId,
        roleIds: editRoleIds,
        fsUserId: editUserFsId.trim() || null,
        quickbooksTimeUserId: (() => {
          const trimmed = editQuickBooksTimeUserId.trim();
          if (trimmed === "") return null;
          const parsed = Number.parseInt(trimmed, 10);
          if (!Number.isFinite(parsed) || parsed <= 0) {
            throw new Error("QuickBooks Time user id must be a positive integer or empty.");
          }
          return parsed;
        })(),
      });
    }

    if (editingRow.staff) {
      await patchStaffMutation.mutateAsync({
        id: editingRow.staff.id,
        firstName: editStaffFirst,
        lastName: editStaffLast,
        email: editStaffEmail.trim() || null,
        active: editStaffActive,
        sbUserId: editStaffSb.trim() || null,
        fsUserId: editStaffFs.trim() || null,
      });
    }
  }

  const counts = useMemo(() => {
    let both = 0;
    let userOnly = 0;
    let staffOnly = 0;
    for (const row of rows) {
      if (row.rowKind === "both") both += 1;
      else if (row.rowKind === "user_only") userOnly += 1;
      else staffOnly += 1;
    }
    return { both, userOnly, staffOnly, total: rows.length };
  }, [rows]);

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <PageTitleWithHelp hoverHint="Hub logins, roles, QuickBooks Time user ids, and profile photos. Employment dates, title, manager, and app access are in People. The icon with a person and X marks rows with no Supabase sign-in.">
            Hub accounts
          </PageTitleWithHelp>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {avatarOperator ? (
            <>
              {!avatarStatusQuery.data?.workspaceConnected ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!avatarStatusQuery.data?.oauthConfigured || connectWorkspaceMutation.isPending}
                  onClick={() => void connectWorkspaceMutation.mutate()}
                >
                  Connect Google Workspace
                </Button>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                disabled={
                  !avatarStatusQuery.data?.workspaceConnected || refreshAllAvatarsMutation.isPending
                }
                onClick={() => void refreshAllAvatarsMutation.mutate()}
              >
                {refreshAllAvatarsMutation.isPending ? "Refreshing avatars…" : "Refresh all avatars"}
              </Button>
            </>
          ) : null}
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
            <Button
              type="button"
              className="shrink-0"
              disabled={!hubWritesAllowed}
              onClick={() => setCreateModalOpen(true)}
            >
              Create user
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </header>

      {avatarActionMessage ? (
        <p className="text-sm text-slate-600" role="status">
          {avatarActionMessage}
        </p>
      ) : null}

      {directoryQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {directoryQuery.error instanceof Error
            ? directoryQuery.error.message
            : "Could not load the directory."}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(
          [
            ["all", "All"],
            ["both", "Integrator + staff"],
            ["user_only", "No staff record"],
            ["staff_only", "Staff only"],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={value}
            variant={filter === value ? "primary" : "secondary"}
            size="sm"
            onClick={() => setFilter(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>
            {counts.total} accounts · {counts.both} with staff · {counts.userOnly} login only · {counts.staffOnly}{" "}
            staff only
          </CardTitle>
          <Input
            type="search"
            value={directorySearch}
            onChange={(event) => setDirectorySearch(event.target.value)}
            placeholder="Filter by name, email, role…"
            aria-label="Filter directory"
            className="w-full sm:max-w-xs"
          />
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Photo</th>
                  <th className="px-3 py-2">Display name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Organisation</th>
                  <th className="px-3 py-2">Roles</th>
                  <th className="px-3 py-2">QBT user</th>
                  <th className="px-3 py-2">Active</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredRows.map((row) => {
                  const key = rowKey(row);
                  const email = row.authEmail ?? row.staff?.email ?? "—";
                  const name = directoryDisplayName(row);
                  const avatarUrl = row.userSetting?.avatarUrl ?? null;
                  const showNoAuthIcon = !hasSupabaseAuthAccount(row);
                  return (
                    <tr key={key}>
                      <td className="px-3 py-2">
                        <HubUserAvatar avatarUrl={avatarUrl} label={name} size="sm" />
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-2 text-slate-900">
                          {showNoAuthIcon ? (
                            <HoverHintContent hoverHint="No Supabase sign-in for this person">
                              <span>
                                <UserRoundX
                                  className="h-4 w-4 shrink-0 text-amber-600"
                                  aria-label="No Supabase sign-in for this person"
                                />
                              </span>
                            </HoverHintContent>
                          ) : null}
                          <span>{name}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{email}</td>
                      <td className="px-3 py-2">
                        {labelChip(organisationLabel(row.userSetting?.orgId, orgs), "neutral")}
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const labels = roleLabels(
                            row.userSetting?.roleIds,
                            row.userSetting?.roleId,
                            roles,
                          );
                          if (labels.length === 0) {
                            return "—";
                          }
                          return (
                            <div className="flex flex-wrap gap-1">
                              {labels.map((text) => labelChip(text, "info"))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {row.userSetting?.quickbooksTimeUserId ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {row.staff ? (row.staff.active ? "Yes" : "No") : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {avatarOperator && row.userId && avatarStatusQuery.data?.workspaceConnected ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={refreshOneAvatarMutation.isPending}
                              onClick={() => void refreshOneAvatarMutation.mutate(row.userId!)}
                            >
                              Refresh photo
                            </Button>
                          ) : null}
                          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!hubWritesAllowed}
                              onClick={() => beginEdit(row)}
                            >
                              Edit
                            </Button>
                          </ReadOnlyHoverLayer>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredRows.length === 0 ? (
            <p className="text-sm text-slate-500">No rows match your filters.</p>
          ) : null}
        </CardBody>
      </Card>

      {editingRow ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeEditModal();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-modal-title"
            className="flex max-h-[min(90vh,56rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h2 id="edit-user-modal-title" className="text-lg font-semibold text-slate-900">
                Edit {directoryDisplayName(editingRow)}
              </h2>
              <Button type="button" variant="ghost" size="sm" onClick={closeEditModal} aria-label="Close dialog">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4">
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
              <fieldset
                disabled={!hubWritesAllowed}
                className="min-h-0 space-y-6 border-0 p-0"
              >
              {editingRow.rowKind === "staff_only" && editingRow.staff ? (
                <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                  {editingRow.staff.sbUserId ? (
                    <p>
                      This staff row is tied to a Supabase account. That account should already have a{" "}
                      <code className="text-xs">user_setting</code> row from signup; find the matching email in this directory
                      and edit organisation or role there. If no row appears, the trigger may be missing on your database or
                      the account predates it—use the HTTP API or SQL to upsert <code className="text-xs">user_setting</code>{" "}
                      for that account.
                    </p>
                  ) : (
                    <p>
                      No Supabase account link on this staff row. Use <strong>Staff record</strong> after the person has
                      an Auth account, or use <strong>Create user</strong> first.
                    </p>
                  )}
                </div>
              ) : null}

              {editingRow.userId && editingRow.userSetting ? (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">Integrator profile</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label htmlFor="detail-display">Display name</Label>
                      <Input
                        id="detail-display"
                        value={editDisplayName}
                        onChange={(event) => setEditDisplayName(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="detail-org">Organisation</Label>
                      <select
                        id="detail-org"
                        value={editOrgId === "" ? "" : String(editOrgId)}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEditOrgId(value === "" ? "" : Number.parseInt(value, 10));
                        }}
                        className={cn(
                          "mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                          "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
                        )}
                      >
                        <option value="">Unset</option>
                        {orgs.map((organisation) => (
                          <option key={organisation.id} value={organisation.id}>
                            {organisation.name ?? `Org ${organisation.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-sm font-medium text-slate-700">Roles</span>
                      <div className="mt-2 flex flex-col gap-2 rounded-md border border-slate-200 p-3">
                        {roles.map((role) => {
                          const checked = editRoleIds.includes(role.id);
                          return (
                            <label key={role.id} className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  if (event.target.checked) {
                                    setEditRoleIds((previous) =>
                                      [...previous, role.id].sort((left, right) => left - right),
                                    );
                                  } else {
                                    setEditRoleIds((previous) =>
                                      previous.filter((id) => id !== role.id),
                                    );
                                  }
                                }}
                                className="rounded border-slate-300"
                              />
                              {role.name ?? `Role ${role.id}`}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="detail-user-fs">Freshsales user id</Label>
                      <Input
                        id="detail-user-fs"
                        value={editUserFsId}
                        onChange={(event) => setEditUserFsId(event.target.value)}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="detail-qbt-user">QuickBooks Time user id</Label>
                      <Input
                        id="detail-qbt-user"
                        value={editQuickBooksTimeUserId}
                        onChange={(event) => setEditQuickBooksTimeUserId(event.target.value)}
                        className="mt-1 font-mono text-xs"
                        inputMode="numeric"
                        placeholder="Leave empty if not mapped"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Links this hub login to QuickBooks Time for the personal time clock. Daily task rows appear only after recent hub station clock activity.
                        Personal QBT email is normal — use the numeric QBT user id, not the work email.
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-rose-100 pt-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={deleteRemoveAuth}
                        onChange={(event) => setDeleteRemoveAuth(event.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Also delete the Supabase Auth user when removing the profile
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-rose-700 hover:bg-rose-50"
                      onClick={() => {
                        if (!editingRow.userId) return;
                        if (!window.confirm("Delete this user_setting row?")) return;
                        deleteUserMutation.mutate({
                          id: editingRow.userId,
                          removeAuth: deleteRemoveAuth,
                        });
                      }}
                      isLoading={deleteUserMutation.isPending}
                    >
                      Delete integrator profile
                    </Button>
                  </div>
                  {patchUserMutation.error ? (
                    <p className="text-sm text-rose-700">{(patchUserMutation.error as Error).message}</p>
                  ) : null}
                  {deleteUserMutation.error ? (
                    <p className="text-sm text-rose-700">{(deleteUserMutation.error as Error).message}</p>
                  ) : null}
                </section>
              ) : null}

              {editingRow.rowKind === "user_only" && editingRow.userId ? (
                <section className="space-y-3 border-t border-slate-100 pt-6">
                  <h3 className="text-sm font-semibold text-slate-900">Create staff row for this user</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="add-s-first">First name</Label>
                      <Input
                        id="add-s-first"
                        value={addStaffFirst}
                        onChange={(event) => setAddStaffFirst(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="add-s-last">Last name</Label>
                      <Input
                        id="add-s-last"
                        value={addStaffLast}
                        onChange={(event) => setAddStaffLast(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={addStaffActive}
                        onChange={(event) => setAddStaffActive(event.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Active
                    </label>
                    <div className="sm:col-span-2">
                      <Label htmlFor="add-s-fs">Freshsales user id (optional)</Label>
                      <Input
                        id="add-s-fs"
                        value={addStaffFs}
                        onChange={(event) => setAddStaffFs(event.target.value)}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!editingRow.userId) return;
                      addStaffMutation.mutate({
                        firstName: addStaffFirst,
                        lastName: addStaffLast,
                        email: editingRow.authEmail?.trim() || null,
                        active: addStaffActive,
                        sbUserId: editingRow.userId,
                        fsUserId: addStaffFs.trim() || null,
                      });
                    }}
                    isLoading={addStaffMutation.isPending}
                    disabled={!addStaffFirst.trim() || !addStaffLast.trim()}
                  >
                    Create staff row
                  </Button>
                  {addStaffMutation.error ? (
                    <p className="text-sm text-rose-700">{(addStaffMutation.error as Error).message}</p>
                  ) : null}
                </section>
              ) : null}

              {editingRow.staff ? (
                <section className="space-y-3 border-t border-slate-100 pt-6">
                  <h3 className="text-sm font-semibold text-slate-900">Staff record</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="staff-e-first">First name</Label>
                      <Input
                        id="staff-e-first"
                        value={editStaffFirst}
                        onChange={(event) => setEditStaffFirst(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="staff-e-last">Last name</Label>
                      <Input
                        id="staff-e-last"
                        value={editStaffLast}
                        onChange={(event) => setEditStaffLast(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="staff-e-email">Email</Label>
                      <Input
                        id="staff-e-email"
                        type="email"
                        value={editStaffEmail}
                        onChange={(event) => setEditStaffEmail(event.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="staff-e-fs">Freshsales user id</Label>
                      <Input
                        id="staff-e-fs"
                        value={editStaffFs}
                        onChange={(event) => setEditStaffFs(event.target.value)}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={editStaffActive}
                        onChange={(event) => setEditStaffActive(event.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Active
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-700 hover:bg-rose-50"
                      onClick={() => {
                        if (!editingRow.staff) return;
                        if (
                          !window.confirm(
                            `Delete staff record for ${editingRow.staff.fullName.trim() || "this person"}?`,
                          )
                        ) {
                          return;
                        }
                        deleteStaffMutation.mutate(editingRow.staff.id);
                      }}
                      isLoading={deleteStaffMutation.isPending}
                    >
                      Delete staff
                    </Button>
                  </div>
                  {patchStaffMutation.error ? (
                    <p className="text-sm text-rose-700">{(patchStaffMutation.error as Error).message}</p>
                  ) : null}
                  {deleteStaffMutation.error ? (
                    <p className="text-sm text-rose-700">{(deleteStaffMutation.error as Error).message}</p>
                  ) : null}
                </section>
              ) : null}
              </fieldset>
              </ReadOnlyHoverLayer>
              <div className="sticky bottom-0 -mx-4 flex justify-end border-t border-slate-200 bg-white px-4 py-3">
                <HubSaveButton
                  size="sm"
                  dirty={directoryDirty}
                  disabled={!hubWritesAllowed}
                  blockedHint={adminReadOnlyTitle}
                  isLoading={isSavingEditingRow}
                  onClick={() => void saveEditingRow().catch(() => undefined)}
                >
                  Save changes
                </HubSaveButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {createModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setCreateModalOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-modal-title"
            className="max-h-[min(90vh,56rem)] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
              <h2 id="create-user-modal-title" className="text-lg font-semibold text-slate-900">
                Create user
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCreateModalOpen(false)}
                aria-label="Close dialog"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <CreateUserForm
                embedded
                onCreated={() => {
                  setCreateModalOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
